import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Mascot } from "@/components/brand/Mascot";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { Brush, GoodPeopleMark } from "@/components/brand/Lettering";
import { Wordmark } from "@/components/brand/Wordmark";
import { Arrow, Barcode, Crown, Divider, Globe, Smiley, Sparkle } from "@/components/brand/Doodles";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { Countdown } from "@/components/Countdown";
import { SignupForm } from "@/components/SignupForm";
import { SotmSpotlight } from "@/components/SotmSpotlight";
import { getProducts } from "@/lib/api";
import { abs, ACCENTS, nextDrop, origin } from "@/lib/site";
import { getDrops } from "@/lib/api";
import { sameAs, useSettings } from "@/context/SettingsContext";

const SPRAY = ["spray-lime", "spray-pink", "spray-sky"];
import { WaitlistForm } from "@/components/WaitlistForm";
import { HomeBundle } from "@/components/ShopExtras";
import { getBundles } from "@/lib/api";

const SLIDES = [
  { lines: ["Same", "same?"], last: "Suspect.", img: "/img/hero_sitting.webp", pos: "55% 35%", alt: "Two models in black and white tees and caps sitting on concrete steps in Toronto" },
  { lines: ["Good", "people. Sus"], last: "ideas.", img: "/img/hero_crew.webp", pos: "60% 25%", alt: "Three friends in the suspects hoodies against a concrete wall" },
  { lines: ["Question", "the"], last: "usual.", img: "/img/look_street_1.webp", pos: "50% 20%", alt: "Model in an olive hoodie walking beside a Toronto streetcar" },
];

const Hero = () => {
  const [i, setI] = useState(0);
  const go = (d) => setI((i + d + SLIDES.length) % SLIDES.length);
  const s = SLIDES[i];
  return (
    <section className="relative isolate w-full overflow-hidden border-b border-ink/10 bg-ink" aria-labelledby="hero-title" data-testid="hero">
      <div className="absolute inset-0 -z-10" data-testid="hero-image">
        {SLIDES.map((sl, k) => (
          <img key={sl.img} src={sl.img} alt={k === i ? sl.alt : ""} aria-hidden={k !== i} fetchPriority={k === 0 ? "high" : "low"} loading={k === 0 ? "eager" : "lazy"} className={`hero-slide photo absolute inset-0 h-full w-full object-cover ${k === i ? "opacity-100" : "opacity-0"}`} style={{ objectPosition: sl.pos }} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink/10" />
        <div className="absolute inset-0 hidden bg-gradient-to-r from-ink/70 via-ink/25 to-transparent lg:block" />
      </div>
      <div className="absolute right-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))] top-10 hidden lg:block"><GoodPeopleMark className="text-[clamp(2.1rem,2.7vw,3.1rem)] text-cloud" susClass="text-cloud/60" testid="hero-brush-accent" /></div>
      <Mascot size={260} label="" className="absolute -bottom-16 -right-12 hidden -rotate-[10deg] lg:block" />
      <div className="relative mx-auto flex min-h-[calc(100svh-88px)] max-w-[1440px] flex-col justify-end px-5 pb-10 pt-28 text-cloud sm:px-10 lg:min-h-[calc(100svh-128px)] lg:pb-14 lg:pt-24">
        <Link to="/drops/sus-world" className="label mb-8 inline-flex items-center gap-2 font-bold transition-opacity hover:opacity-60" data-testid="hero-latest-drop-cta">Latest drop · 002 Sus World <ArrowRight size={14} /></Link>
        <h1 id="hero-title" key={i} className="display text-[clamp(4.25rem,9.2vw,8.75rem)] animate-in fade-in duration-700">
          {s.lines.map((l) => <span key={l} className="block">{l}</span>)}
          <span className="block"><span className={`spray-hero font-brush ${SPRAY[i % 3]}`} data-testid="hero-spray-word">{s.last}</span></span>
        </h1>
        <p className="font-a mt-10 text-[17px] leading-snug">Clothing for<br />the curious kind.</p>
        <div className="mt-8 flex flex-wrap items-center gap-8">
          <Link to="/shop" className="btn btn-lime font-a !text-[15px] !tracking-normal" data-testid="hero-shop-cta">Shop the drop <ArrowRight size={16} /></Link>
          <Link to="/lookbook" className="link-u font-a !text-[15px]" data-testid="hero-watch-film"><Play size={12} fill="currentColor" /> Watch the film</Link>
        </div>
        <div className="mt-12 flex items-center gap-5" data-testid="hero-slide-indicator">
          <button onClick={() => go(-1)} aria-label="Previous slide" className="transition-opacity hover:opacity-60" data-testid="hero-prev"><ChevronLeft size={18} strokeWidth={1.5} /></button>
          <span className="font-a text-[15px]" data-testid="hero-slide-count">0{i + 1} <span className="opacity-40">/ 0{SLIDES.length}</span></span>
          <button onClick={() => go(1)} aria-label="Next slide" className="transition-opacity hover:opacity-60" data-testid="hero-next"><ChevronRight size={18} strokeWidth={1.5} /></button>
        </div>
      </div>
    </section>
  );
};

const CATS = [["tees", "Tees", "/img/og_icon_tee.webp"], ["hoodies", "Hoodies", "/img/question_hoodie.webp"], ["caps", "Caps", "/img/certified_sus_cap.webp"]];

const MobileCats = () => (
  <nav aria-label="Shop by category" className="grid grid-cols-3 gap-3 border-b border-ink/10 px-5 py-6 lg:hidden">
    {CATS.map(([id, l, img]) => (
      <Link key={id} to={`/shop?category=${id}`} className="block text-center" data-testid={`mobile-category-${id}`}>
        <span className={`block aspect-square overflow-hidden rounded-[22%] border-[1.5px] border-ink ${ACCENTS[CATS.findIndex((c) => c[0] === id)]}`}><img src={img} alt="" className="h-full w-full object-cover mix-blend-multiply" loading="lazy" /></span>
        <span className="label mt-3 block font-bold">{l}</span>
      </Link>
    ))}
  </nav>
);

const PERKS = [[Barcode, "Free shipping", "Over C$100"], [Crown, "Limited drops", "Always fresh"], [Globe, "Canada based", "Ships across Canada"], [Sparkle, "Original designs", "Not mass produced"]];

const Perks = () => (
  <section aria-label="Perks" className="border-b border-ink/10">
    <ul className="mx-auto grid max-w-[1440px] grid-cols-2 gap-px bg-ink/10 lg:grid-cols-4">
      {PERKS.map(([Icon, t, s], i) => (
        <li key={t} className="flex flex-col items-start gap-4 bg-cloud px-5 py-7 sm:flex-row sm:items-center sm:gap-5 sm:px-10 sm:py-8" data-testid={`perk-${i}`}>
          <Icon size={38} className="shrink-0" aria-hidden="true" />
          <p className="label"><span className="font-display text-[19px] uppercase leading-none tracking-[0.02em]">{t}</span><br /><span className="text-ink/55">{s}</span></p>
        </li>
      ))}
    </ul>
  </section>
);

const LatestDrop = ({ products }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-10 lg:py-32" aria-labelledby="latest-title">
    <Reveal className="grid items-end gap-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="label text-ink/55">Featured collection</p>
        <h2 id="latest-title" className="display mt-5 flex items-end gap-3 text-[clamp(3.5rem,8vw,8.75rem)]"><span>Latest <span className="hl hl-pink hl-thick">drop</span></span> <ArrowDownRight className="mb-[0.08em] h-[0.6em] w-[0.6em]" strokeWidth={1.25} aria-hidden="true" /></h2>
      </div>
      <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-end sm:justify-between lg:col-span-5 lg:justify-end lg:gap-14">
        <p className="max-w-[260px] text-sm leading-relaxed text-ink/70">New ideas. Same suspects. Limited quantities, unlimited possibilities.</p>
        <Link to="/shop" className="link-u shrink-0" data-testid="featured-view-all">View all <ArrowRight size={14} /></Link>
      </div>
    </Reveal>
    <div className="mt-16 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-testid="featured-grid">
      {products.slice(0, 4).map((p, i) => <Reveal key={p.slug} delay={i * 80}><ProductCard product={p} index={i} /></Reveal>)}
    </div>
  </section>
);

const Campaign = () => (
  <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-10 lg:pb-32" aria-labelledby="campaign-title" data-testid="campaign-row">
    <div className="flex items-end justify-between gap-6 border-t border-ink/10 pt-10">
      <h2 id="campaign-title" className="label font-bold">The campaign <span className="hidden text-ink/60 sm:inline">· Normal is boring.</span></h2>
      <Link to="/lookbook" className="link-u shrink-0" data-testid="campaign-lookbook-link">Lookbook <ArrowRight size={14} /></Link>
    </div>
    <div className="no-scrollbar -mx-5 mt-8 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-6 sm:overflow-visible sm:px-0">
      <Reveal className="relative flex aspect-[2/3] w-[78vw] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-lime p-6 sm:w-auto" data-testid="poster-normal">
        <p className="display text-[clamp(3rem,6.2vw,5.75rem)]">Normal<br />is boring.</p>
        <Mascot size={200} label="" className="mx-auto h-auto w-[58%] -rotate-[6deg]" />
        <span className="label font-bold">Certified sus · 001</span>
      </Reveal>
      <Reveal delay={90} className="relative flex aspect-[2/3] w-[78vw] shrink-0 snap-start flex-col items-center justify-between overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-pink p-8 sm:w-auto" data-testid="poster-sus-vibes">
        <div className="flex w-full justify-between"><Crown size={44} className="-rotate-12" /><Crown size={30} className="rotate-12" /></div>
        <Brush lines={["Sus", "vibes", "only"]} halo={false} className="text-center text-[clamp(3.5rem,7vw,6.5rem)]" />
        <Wordmark variant="stacked" />
      </Reveal>
      <Reveal delay={180} className="relative aspect-[2/3] w-[78vw] shrink-0 snap-start overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-sky sm:w-auto" data-testid="poster-question">
        <img src="/img/poster_question_hoodie.webp" alt="Back of a grey hoodie printed with QUESTION THE USUAL in black brush lettering" loading="lazy" className="photo absolute inset-0 h-full w-full object-cover mix-blend-multiply" />
        <Mascot size={56} label="" fill="#FF7EC7" className="absolute left-5 top-5" />
        <span className="absolute bottom-5 left-5"><Wordmark mascot={false} /></span>
      </Reveal>
    </div>
  </section>
);

const CrewBanner = () => (
  <section id="signup" className="mx-auto max-w-[1440px] scroll-mt-32 px-5 py-16 sm:px-10 lg:py-24" aria-labelledby="signup-title">
    <div className="grid overflow-hidden rounded-[24px] border-[1.5px] border-ink lg:grid-cols-2" data-testid="crew-banner">
    <div className="relative aspect-[4/5] overflow-hidden sm:aspect-[16/10] lg:aspect-auto lg:min-h-[680px]">
      <img src="/img/sus_vibes_wall.webp" alt="Back view of a black hoodie in front of a concrete wall with SUS VIBES ONLY graffiti" loading="lazy" className="photo absolute inset-0 h-full w-full object-cover" />
    </div>
    <div className="flex flex-col justify-center bg-lime px-5 py-20 sm:px-10 lg:px-20">
      <MascotDoodle variant="crown" size={130} bg="#D7FF5B" />
      <h2 id="signup-title" className="display mt-8 text-[clamp(3.25rem,6vw,6.5rem)]">Be part of<br />the <span className="hl hl-pink hl-thick">sus crew</span></h2>
      <p className="mt-6 text-[15px]">Early access. Special drops. No spam.</p>
      <div className="mt-10 max-w-md"><SignupForm source="home" testid="waitlist" /></div>
    </div>
    </div>
  </section>
);

const Upcoming = () => {
  const [drop, setDrop] = useState(null);
  const [live, setLive] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    getDrops().then((d) => {
      const n = nextDrop(d) || null;
      const l = d.filter((x) => x.status === "live").sort((a, b) => new Date(b.release_date) - new Date(a.release_date))[0];
      setDrop(n); setLive(l && (!n || new Date(l.release_date) >= new Date(n.release_date) - 864e5 * 60) ? l : null);
    }).catch(() => {});
  }, []);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const liveDrop = live || (drop && new Date(drop.release_date) <= Date.now() ? drop : null);
  if (liveDrop) {
    return (
      <section className="border-b border-ink bg-lime" aria-labelledby="upcoming-title" data-testid="home-upcoming">
        <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-24 sm:px-10 lg:grid-cols-12 lg:py-32" data-testid="home-drop-live">
          <Reveal className="lg:col-span-8">
            <h2 id="upcoming-title" className="label font-bold">Drop {liveDrop.number} · {liveDrop.name}</h2>
            <p className="display mt-6 text-[clamp(4.5rem,12vw,10rem)]">Live now</p>
            <Link to={`/drops/${liveDrop.slug}`} className="btn btn-ink mt-10" data-testid="home-shop-live-drop">Shop the drop <ArrowRight size={16} /></Link>
          </Reveal>
          <Reveal delay={120} className="flex justify-center lg:col-span-4 lg:justify-end"><MascotDoodle variant="crown" size={380} bg="#FF7EC7" className="w-[75%] max-w-[380px] lg:w-full" /></Reveal>
        </div>
      </section>
    );
  }
  const when = drop && new Date(drop.release_date).toLocaleString("en-CA", { timeZone: "America/Toronto", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <section className="border-b border-ink bg-sky" aria-labelledby="upcoming-title" data-testid="home-upcoming">
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 py-24 sm:px-10 lg:grid-cols-12 lg:py-32">
        <Reveal className="lg:col-span-8">
          <h2 id="upcoming-title" className="label font-bold" data-testid="home-upcoming-label">Upcoming drop {drop && <Link to={`/drops/${drop.slug}`} className="text-ink/70 underline-offset-4 hover:underline" data-testid="home-upcoming-drop-link">· {drop.number} {drop.name} · {when} ET</Link>}</h2>
          <div className="mt-8"><Countdown target={drop?.release_date} testid="home-countdown" /></div>
          {drop && <div className="mt-12" data-testid="join-waitlist-button"><WaitlistForm drop={drop} testid="home-waitlist" /></div>}
        </Reveal>
        <Reveal delay={120} className="flex justify-center lg:col-span-4 lg:justify-end">
          <MascotDoodle variant="peace" size={420} bg="#FF7EC7" label="Pink mascot winking and throwing peace signs" className="w-[80%] max-w-[420px] lg:w-full" />
        </Reveal>
      </div>
    </section>
  );
};

const LookTeaser = () => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-10 lg:py-32" aria-labelledby="look-title">
    <div className="flex items-end justify-between gap-6">
      <h2 id="look-title" className="label font-bold">Lookbook <span className="text-ink/45">· Vol. 01 Toronto</span></h2>
      <Link to="/lookbook" className="link-u" data-testid="lookbook-teaser-link">View lookbook <ArrowRight size={14} /></Link>
    </div>
    <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6">
      {["/img/look_6.webp", "/img/look_7.webp", "/img/look_street_5.webp"].map((src, i) => (
        <Reveal key={src} delay={i * 80} className="group overflow-hidden rounded-[16px] sm:rounded-[24px]">
          <img src={src} alt="the suspects lookbook street photo in Toronto" loading="lazy" className="photo aspect-[3/4] w-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]" />
        </Reveal>
      ))}
    </div>
  </section>
);

const Story = () => (
  <section aria-labelledby="story-title">
    <Divider icon={Smiley} />
    <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-24 sm:px-10 lg:grid-cols-2 lg:gap-24 lg:py-32">
      <Reveal className="relative overflow-hidden rounded-[24px]">
        <img src="/img/story_hoodie_back.webp" alt="Back view of a cream hoodie with a small back print against a pale concrete wall" loading="lazy" className="photo aspect-[4/5] w-full object-cover" />
        <Brush lines={["Question", "the usual."]} className="absolute bottom-10 left-8 text-[clamp(2.25rem,4vw,3.5rem)] text-ink" />
      </Reveal>
      <Reveal delay={120}>
        <p className="label text-ink/55">Our story</p>
        <h2 id="story-title" className="display relative mt-6 text-[clamp(3rem,6vw,6.5rem)]">
          Built for the<br /><span className="hl hl-sky hl-thick">curious kind.</span>
          <Arrow className="absolute -bottom-6 right-0 hidden h-12 w-16 sm:block lg:-right-4" />
        </h2>
        <p className="mt-10 max-w-md text-[15px] leading-relaxed text-ink/75">We started in Toronto in 2024 with one question: why does everything look the same? the suspects is for people who side-eye the algorithm, collect weird ideas and keep good people close. Every graphic is drawn by hand. Every drop is small.</p>
        <Link to="/about" className="btn btn-ink mt-10" data-testid="story-about-link">Our story <ArrowRight size={16} /></Link>
      </Reveal>
    </div>
  </section>
);

const jsonLd = (settings) => [
  {
    "@context": "https://schema.org", "@type": "Organization", name: "the suspects", url: origin(), logo: abs("/logo.svg"),
    slogan: "Good People. Sus Ideas.", foundingDate: "2024", description: "Gen Z streetwear brand from Toronto, Canada. Clothing for the curious kind.",
    address: { "@type": "PostalAddress", addressLocality: "Toronto", addressRegion: "ON", addressCountry: "CA" },
    ...(sameAs(settings).length ? { sameAs: sameAs(settings) } : {}),
  },
  {
    "@context": "https://schema.org", "@type": "WebSite", name: "the suspects", url: origin(),
    potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${origin()}/shop?q={search_term_string}` }, "query-input": "required name=search_term_string" },
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const settings = useSettings();
  useEffect(() => { getProducts({ featured: true }).then(setFeatured).catch(() => {}); }, []);
  const [bundle, setBundle] = useState(null);
  useEffect(() => { getBundles().then((b) => setBundle(b[0] || null)).catch(() => {}); }, []);
  return (
    <div data-testid="home-page">
      <Seo description="the suspects: Gen Z streetwear from Toronto, est. 2024. Heavyweight tees, hoodies and accessories with original art. Limited drops. Free shipping in Canada over C$100." path="/" jsonLd={jsonLd(settings)} />
      <Hero />
      <MobileCats />
      <Perks />
      <LatestDrop products={featured} />
      <HomeBundle b={bundle} />
      <Campaign />
      <SotmSpotlight />
      <CrewBanner />
      <Upcoming />
      <LookTeaser />
      <Story />
    </div>
  );
}
